import React, { useEffect, useState, useMemo } from 'react';
import {
  QButton,
  QButtonGroup,
  QBodyLayout,
  QHeader,
  QTitle,
  QSubtitle,
  deprecated,
  QMenuButton,
  QMenuItemType,
  QSpinner,
  QText,
  QInput,
  QBox,
  QIcon,
  QIconButton,
  useCurrentUser as useQCurrentUser,
} from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { Group, GroupRowData } from '../../types';
import { CreateGroupModal } from '../modals';
import { EditGroupModal } from '../modals/EditGroupModal';
import { DeleteGroupModal } from '../modals/DeleteGroupModal';
import { getGroups } from '../../api/medtech-api';
import { AssignManagersModal } from '../modals/AssignManagers';

type GroupQMenuItemType = QMenuItemType & { group: Group };

export const Groups: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const [isCreateGroupModalOpen, setCreateGroupModalOpen] = useState(false);
  const [isEditGroupModalOpen, setEditGroupModalOpen] = useState(false);
  const [isDeleteGroupModalOpen, setDeleteGroupModalOpen] = useState(false);
  const [isAssignManagersModalOpen, setAssignManagersModalOpen] = useState(false);
  const [clickedGroup, setClickedGroup] = useState<Group>();
  const [groups, setGroups] = useState<GroupRowData[]>();
  const [searchQuery, setSearchQuery] = useState('');
  const { companyId: currentCompanyId } = useQCurrentUser();

  const columns = React.useMemo(
    () => [
      {
        Header: t('groups.table.groupName'),
        accessor: 'name',
      },
      {
        Header: t('groups.table.groupManagers'),
        accessor: 'managers',
      },
      {
        Header: t('groups.table.includeInReports'),
        accessor: 'includeInReports',
      },
      {
        Header: '',
        accessor: 'actions',
        disableSortBy: true,
        width: '5%',
      },
    ],
    [t],
  );

  const filteredGroups = useMemo(() => {
    if (!searchQuery || !groups) return groups;

    const query = searchQuery.toLowerCase();
    return groups.filter((group) => {
      const nameMatch = group.name.toLowerCase().includes(query);
      const managersMatch = group.managers.toLowerCase().includes(query);
      return nameMatch || managersMatch;
    });
  }, [groups, searchQuery, t]);

  const handleAction = (action: GroupQMenuItemType) => {
    setClickedGroup(action.group);
    const { id: actionItemId } = action;
    if (actionItemId === 'editGroup') {
      setEditGroupModalOpen(true);
    }
    if (actionItemId === 'deleteGroup') {
      setDeleteGroupModalOpen(true);
    }
    if (actionItemId === 'assignManagers') {
      setAssignManagersModalOpen(true);
    }
  };

  const formatGroups = (groupsData: Group[]) => {
    return groupsData.map((group) => {
      return {
        id: group.id,
        name: group.name,
        managers: group.managers?.map((manager) => manager.name).join(', ') || t('groups.noManagers'),
        includeInReports: group.include_in_reports ? t('common.yes') : t('common.no'),
        actions: (
          <QMenuButton
            buttonLabel=""
            iconName="MoreVertical"
            itemSize="sm"
            items={[
              {
                id: 'assignManagers',
                label: t('groups.assignManagers'),
                group,
              } as GroupQMenuItemType,
              {
                id: 'editGroup',
                label: t('groups.editGroup'),
                group,
              } as GroupQMenuItemType,
              {
                id: 'deleteGroup',
                label: t('groups.deleteGroup'),
                group,
              } as GroupQMenuItemType,
            ]}
            onItemClick={(item) => handleAction(item as GroupQMenuItemType)}
            variant="icon"
          />
        ),
      };
    });
  };

  const fetchGroups = async (companyId: number) => {
    setLoading(true);

    const { data } = await getGroups(companyId);

    setGroups(formatGroups(data));
    setLoading(false);
  };

  useEffect(() => {
    fetchGroups(currentCompanyId);
  }, [currentCompanyId]);

  if (isLoading) {
    return <QSpinner size="sm" />;
  }

  return (
    <QBodyLayout.Default>
      <QHeader>
        <QTitle>{t('nav.groups')}</QTitle>
        <QSubtitle>{t('groups.subtitle')}</QSubtitle>
        <QButtonGroup>
          <QButton variant="solid" data-cy="create-new-group-button" onClick={() => setCreateGroupModalOpen(true)}>
            {t('groups.createNewGroup')}
          </QButton>
        </QButtonGroup>
      </QHeader>
      <QBox position="relative" width="50%" mb="1em">
        <QBox position="absolute" left="12px" top="50%" transform="translateY(-50%)" pointerEvents="none">
          <QIcon iconName="Search" color="gray.400" />
        </QBox>
        <QInput
          placeholder={t('groups.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-cy="search-groups-input"
          pl="40px"
          pr={searchQuery ? '40px' : undefined}
          sx={{
            '&:focus': {
              borderColor: 'gray.300',
              boxShadow: '0 0 0 1px gray.300',
            },
          }}
        />
        {searchQuery && (
          <QBox position="absolute" right="8px" top="50%" transform="translateY(-50%)" zIndex="1">
            <QIconButton
              aria-label="Clear search"
              iconName="X"
              size="sm"
              variant="ghost"
              onClick={() => setSearchQuery('')}
              data-cy="clear-search-button"
              color="gray.400"
            />
          </QBox>
        )}
      </QBox>
      <deprecated.QDataTable columns={columns} data={filteredGroups!} hasPagination={false} />
      {!filteredGroups?.length ? <QText>{t('emptyStates.noData')}</QText> : null}
      {isCreateGroupModalOpen ? (
        <CreateGroupModal
          closeModal={() => setCreateGroupModalOpen(false)}
          currentGroups={groups!}
          setRefetch={() => fetchGroups(currentCompanyId)}
        />
      ) : null}
      {isEditGroupModalOpen ? (
        <EditGroupModal
          closeModal={() => setEditGroupModalOpen(false)}
          group={clickedGroup!}
          currentGroups={groups!}
          setRefetch={() => fetchGroups(currentCompanyId)}
        />
      ) : null}
      {isDeleteGroupModalOpen ? (
        <DeleteGroupModal
          closeModal={() => setDeleteGroupModalOpen(false)}
          group={clickedGroup!}
          setRefetch={() => fetchGroups(currentCompanyId)}
        />
      ) : null}
      {isAssignManagersModalOpen ? (
        <AssignManagersModal
          isOpen={isAssignManagersModalOpen}
          closeModal={() => setAssignManagersModalOpen(false)}
          group={clickedGroup!}
          setRefetch={() => fetchGroups(currentCompanyId)}
        />
      ) : null}
    </QBodyLayout.Default>
  );
};
